import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from './SEO';

/* Subtle scroll-triggered fade/translate. No heavy animation per brief. */
const Reveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* ── Section spacer with full-width nature photo (placeholder) ── */
const PhotoBand = ({ src, alt }) => (
  <div
    className="relative w-full h-[320px] md:h-[420px] lg:h-[520px] overflow-hidden bg-[#2d3a1a]"
    data-testid="about-photo-band"
  >
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 w-full h-full object-cover"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20" />
  </div>
);

const AboutPage = () => {
  return (
    <div className="bg-[#f7f4ec] text-[#1B1B1B]">
      <SEO
        title="Hakkımızda"
        description="Camption, açık hava ve eko-turizm odaklı butik bir trekking seyahat markasıdır. Likya Yolu başta olmak üzere Türkiye'nin doğa rotalarını önce biz gider, sonra sizin için derleriz."
      />

      {/* ─────────── 1. Hero ─────────── */}
      <section
        className="relative w-full h-[88vh] min-h-[560px] flex items-end overflow-hidden"
        data-testid="about-hero"
      >
        <img
          alt="Camption ekibinin yürüdüğü patika manzarası"
          src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=2000&q=80"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/70" />
        <div className="relative w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
          <Reveal>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] font-semibold text-[#C8D843] mb-4">
              Hakkımızda
            </p>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] max-w-3xl"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Önce biz gideriz.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/90">
              Bir rota düşünün. Yüzlerce kilometre patika, onlarca koy, sayısız antik kent, irili ufaklı pansiyon. Hangi etabı yürümeli, nerede mola vermeli, akşam nerede kalmalı? Camption bu soruların cevabı.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─────────── 2. Nasıl Çalışıyoruz ─────────── */}
      <section className="py-20 md:py-28 bg-[#f7f4ec]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-4">
              Nasıl çalışıyoruz
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight mb-8"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Masa başında değil, patikanın üzerinde planlarız.
            </h2>
            <p className="text-lg leading-relaxed text-[#2a2a2a]">
              Biz bir rotayı masa başında planlamayız. Önce kendimiz gideriz. Patikaları defalarca yürür, koyları, manzara noktalarını ve tesisleri tek tek deneriz. En güzelini işaretler, gerisini eler, sonra hepsini sizin için tek bir akışta toplarız. Elinize ulaşan program, aylarca süren bu keşfin damıtılmış hâlidir.
            </p>
          </Reveal>
        </div>
      </section>

      <PhotoBand
        alt="Likya Yolu kıyı patikası"
        src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=2000&q=80"
      />

      {/* ─────────── 3. Rehber / Kurucu (iki sütun) ─────────── */}
      <section className="py-20 md:py-28 bg-[#fffdf6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Portre placeholder */}
            <Reveal className="order-1">
              <div className="relative aspect-[4/5] w-full max-w-[440px] mx-auto md:mx-0 overflow-hidden rounded-2xl bg-[#3D4A26]/10 ring-1 ring-black/5">
                <img
                  alt="Baturalp, Camption kurucusu ve baş rehber"
                  src="https://images.unsplash.com/photo-1521336575822-6da63fb45455?w=900&q=80"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>

            <Reveal className="order-2">
              <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-4">
                Rehber ve kurucu
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#1B1B1B] leading-tight mb-6"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Baturalp.
              </h2>
              <p className="text-lg leading-relaxed text-[#2a2a2a]">
                Ekibin başında Baturalp var. On yıldır bu coğrafyayı adımlayan, eko-turizm ve dağcılık eğitimi almış bir rehber. Yürürken duyduğunuz her hikâye kitaptan değil, o taşların üstünden defalarca geçmiş birinden gelir.
              </p>
              <div className="mt-8 h-[3px] w-12 bg-[#C8D843] rounded-full" />
            </Reveal>
          </div>
        </div>
      </section>

      <PhotoBand
        alt="Patikadan akşam ışığıyla deniz manzarası"
        src="https://images.unsplash.com/photo-1501554728187-ce583db33af7?w=2000&q=80"
      />

      {/* ─────────── 4. Model ─────────── */}
      <section className="py-20 md:py-28 bg-[#f7f4ec]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-4">
              Modelimiz
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight mb-8"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Siz sadece keyfini çıkarın
            </h2>
            <p className="text-lg leading-relaxed text-[#2a2a2a]">
              Yola çıktığınız andan itibaren misafirimizsiniz. Valiziniz her akşam bir sonraki konaklamada sizi bekler, ekipmanınız hazır olur, yolun her dönemecini sizden önce biz yürürüz. Geriye size kalan tek şey, durup derin bir nefes almak ve manzaranın tadını çıkarmak.
            </p>
          </Reveal>
        </div>
      </section>

      <PhotoBand
        alt="Çadır ve sırt çantasıyla kamp alanı"
        src="https://images.unsplash.com/photo-1533240332313-0db49b459ad6?w=2000&q=80"
      />

      {/* ─────────── 5. Vizyon ─────────── */}
      <section className="py-20 md:py-28 bg-[#fffdf6]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-4">
              Vizyon
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight mb-8"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Likya Yolu başlangıç noktası.
            </h2>
            <p className="text-lg leading-relaxed text-[#2a2a2a]">
              Likya Yolu işin başlangıç noktası, sonu değil. Camption'ı bir doğa ve açık hava markası olarak kuruyoruz. Türkiye'nin keşfedilmeyi bekleyen rotalarını birer birer derleyip önünüze getireceğiz. Liste uzadıkça mantık değişmeyecek: en iyisini bulmak, gerisini sadeleştirmek.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─────────── 6. Kapanış + CTA ─────────── */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#0f1a0a]">
        <img
          alt="Geniş doğa manzarası"
          src="https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=2000&q=80"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1a0a]/70 via-[#0f1a0a]/80 to-[#0f1a0a]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-10 max-w-2xl mx-auto"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Şehirden çıkıp gerçekten başka bir yerde olmak isteyenler, doğru yerdesiniz.
            </h2>
            <Link
              to="/turlar"
              data-testid="about-cta-tours"
              className="inline-flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-bold px-8 py-4 rounded-full text-base hover:bg-white transition-colors duration-300"
            >
              Turları Keşfet
              <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
